// ============================================
// PIPELINE VIEW TYPES (HubSpot-szerű)
// ============================================

export type PipelineStage = 'quotations' | 'orders' | 'deliveries' | 'invoices';

export interface PipelineCard {
  id: string;
  organizationId: string; // Multi-tenant - KÖTELEZŐ
  
  // Dokumentum típus és azonosítók
  documentType: 'quotation' | 'order' | 'delivery_note' | 'invoice';
  documentNumber: string;
  stage: PipelineStage;
  
  // Alapadatok
  customerName: string;
  customerId: string;
  totalAmount: number;
  currency: string;
  
  // Státusz
  status: string;
  statusColor?: string;
  
  // Dátumok
  createdAt: Date;
  updatedAt: Date;
  dueDate?: Date;
  isOverdue?: boolean;
  
  // Dokumentum kapcsolatok (hierarchia)
  quotationId?: string;
  orderId?: string;
  deliveryNoteId?: string;
  invoiceId?: string;
  
  // Projekt kapcsolat
  projectId?: string;
  projectName?: string;
  projectKey?: string;
  
  // Csatolt dokumentumok
  documentCount?: number;
  
  // Megjegyzések és tagek
  notes?: string;
  tags?: string[];
  
  // Hozzárendelt felhasználók
  assignedUsers?: string[];
  assignedUsersNames?: string[];
  
  // Jogosultságok (ki láthatja)
  visibility?: 'public' | 'private' | 'team';
  allowedRoles?: ('admin' | 'user' | 'customer')[];
}

export interface PipelineStageData {
  stage: PipelineStage;
  title: string;
  cards: PipelineCard[];
  totalValue: number;
  count: number;
  color: string;
  icon?: string;
}

export interface PipelineMetrics {
  organizationId: string; // Multi-tenant
  
  // Összesítések
  totalValue: number;
  totalCount: number;
  currency: string;
  
  // Stage-enkénti összesítések
  stages: {
    quotations: { count: number; value: number };
    orders: { count: number; value: number };
    deliveries: { count: number; value: number };
    invoices: { count: number; value: number };
  };
  
  // Konverziós ráták
  conversionRate: {
    quotationToOrder: number; // %
    orderToDelivery: number; // %
    deliveryToInvoice: number; // %
    overall: number; // Quotation → Invoice %
  };
  
  // Átlagok
  averageValue: number;
  averageDealSize: {
    quotations: number;
    orders: number;
    deliveries: number;
    invoices: number;
  };
  
  // Átlagos időtartamok (napokban)
  averageDuration: {
    quotationToOrder: number;
    orderToDelivery: number;
    deliveryToInvoice: number;
    totalCycleTime: number; // Quotation → Invoice
  };
  
  // Időszak alapú összehasonlítás
  periodComparison?: {
    previousPeriod: {
      totalValue: number;
      totalCount: number;
      conversionRate: number;
    };
    change: {
      value: number; // %
      count: number; // %
      conversionRate: number; // %
    };
  };
}

export interface PipelineFilters {
  organizationId?: string; // Multi-tenant szűrés - KÖTELEZŐ
  
  // Dokumentum szűrők
  customerId?: string;
  projectId?: string;
  
  // Dátum szűrők
  dateFrom?: Date;
  dateTo?: Date;
  
  // Érték szűrők
  minValue?: number;
  maxValue?: number;
  currency?: string;
  
  // Státusz szűrők
  status?: string;
  statuses?: string[];
  
  // Egyéb szűrők
  tags?: string[];
  assignedUser?: string;
  searchTerm?: string;
  
  // Jogosultság alapú szűrés
  visibility?: 'public' | 'private' | 'team';
  onlyMyCards?: boolean; // Csak a felhasználóhoz rendelt kártyák
}

export interface PipelineCardAction {
  type: 'move' | 'convert' | 'edit' | 'delete' | 'archive';
  cardId: string;
  fromStage?: PipelineStage;
  toStage?: PipelineStage;
  userId: string; // Ki hajtotta végre
  timestamp: Date;
  metadata?: any;
}

// ============================================
// PIPELINE PERMISSIONS
// ============================================

export interface PipelinePermissions {
  canView: boolean; // Láthatja a pipeline-t
  canEdit: boolean; // Szerkesztheti a kártyákat
  canMove: boolean; // Mozgathatja a kártyákat
  canConvert: boolean; // Konvertálhatja a dokumentumokat (Q→O, O→D, D→I)
  canDelete: boolean; // Törölheti a kártyákat
  canExport: boolean; // Exportálhatja az adatokat
}

// ============================================
// PIPELINE SETTINGS
// ============================================

export interface PipelineSettings {
  organizationId: string; // Multi-tenant
  
  // Stage beállítások
  stages: {
    quotations: { enabled: boolean; color: string; icon?: string };
    orders: { enabled: boolean; color: string; icon?: string };
    deliveries: { enabled: boolean; color: string; icon?: string };
    invoices: { enabled: boolean; color: string; icon?: string };
  };
  
  // Automatizálás beállítások
  automation: {
    autoConvertQuotationToOrder: boolean;
    autoConvertOrderToDelivery: boolean;
    autoConvertDeliveryToInvoice: boolean;
    notifyOnStageChange: boolean;
    notifyOnOverdue: boolean;
  };
  
  // Megjelenítési beállítások
  display: {
    showMetrics: boolean;
    showFilters: boolean;
    defaultView: 'kanban' | 'list' | 'calendar';
    cardsPerPage: number;
  };
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
}

// ============================================
// HELPER TYPES
// ============================================

export const PIPELINE_STAGE_CONFIG = {
  quotations: {
    title: 'Ajánlatok',
    color: '#3B82F6', // blue-500
    icon: 'DocumentTextIcon'
  },
  orders: {
    title: 'Megrendelések',
    color: '#8B5CF6', // purple-500
    icon: 'ShoppingCartIcon'
  },
  deliveries: {
    title: 'Szállítólevelek',
    color: '#F59E0B', // amber-500
    icon: 'TruckIcon'
  },
  invoices: {
    title: 'Számlák',
    color: '#10B981', // green-500
    icon: 'CurrencyDollarIcon'
  }
} as const;

export const DEFAULT_PIPELINE_SETTINGS: Omit<PipelineSettings, 'organizationId' | 'createdAt' | 'updatedAt' | 'updatedBy'> = {
  stages: {
    quotations: { enabled: true, color: PIPELINE_STAGE_CONFIG.quotations.color },
    orders: { enabled: true, color: PIPELINE_STAGE_CONFIG.orders.color },
    deliveries: { enabled: true, color: PIPELINE_STAGE_CONFIG.deliveries.color },
    invoices: { enabled: true, color: PIPELINE_STAGE_CONFIG.invoices.color }
  },
  automation: {
    autoConvertQuotationToOrder: false,
    autoConvertOrderToDelivery: false,
    autoConvertDeliveryToInvoice: false,
    notifyOnStageChange: true,
    notifyOnOverdue: true
  },
  display: {
    showMetrics: true,
    showFilters: true,
    defaultView: 'kanban',
    cardsPerPage: 20
  }
};
